/**
 * Garage Settings View
 * User preferences and configuration
 */

import React, { useState, useEffect } from 'react';
import { User as UserIcon, RefreshCw, RotateCcw, LogOut } from 'lucide-react';
import { TEAM_OPTIONS, ROLE_OPTIONS, HUB_ORGANIZATION_OPTIONS } from '../constants/onboardingOptions';
import garageAPI from '../../../services/garage/api';
import authService from '../../../services/authService';
import ConfirmModal from '../modals/ConfirmModal';
import AlertModal from '../modals/AlertModal';
import './GarageSettingsView.css';
import GarageLoadingScreen from '../components/GarageLoadingScreen';

/** Split a display name into first / last when profile firstName/lastName are missing. */
const splitDisplayName = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
};

const resolveNameFields = (profile, fallbackName) => {
  const first = (profile?.firstName || '').trim();
  const last = (profile?.lastName || '').trim();
  if (first || last) {
    return { firstName: first, lastName: last };
  }
  return splitDisplayName(profile?.displayName || fallbackName);
};

const GarageSettingsView = ({ userEmail, userName, userInitials, onProfileUpdate, configVersion = 0 }) => {
  const email = userEmail; // For API calls
  const [userProfile, setUserProfile] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  // Department is Workday-derived and admin-managed — displayed read-only here
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [workdayBusinessUnit, setWorkdayBusinessUnit] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Dynamic options
  const [dynamicRoles, setDynamicRoles] = useState({ leadership: [], operations: [] });
  const [dynamicTeams, setDynamicTeams] = useState([]);
  const [dynamicDepartments, setDynamicDepartments] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);

  const departmentLabel =
    dynamicDepartments.find((d) => d.id === selectedDepartment)?.label ||
    selectedDepartment ||
    workdayBusinessUnit ||
    'Not assigned yet';

  // Modal states
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    variant: 'default'
  });

  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  });
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await authService.signOut();
    } catch (err) {
      console.error('Sign out failed', err);
      setSigningOut(false);
      setAlertModal({
        isOpen: true,
        title: 'Sign out failed',
        message: err.message || 'Could not sign out. Please try again.',
        variant: 'error',
      });
    }
  };

  // Load dynamic roles and teams - reload when config changes
  useEffect(() => {
    const loadDynamicOptions = async () => {
      setRolesLoading(true);
      try {
        const [roleOptions, teamOptions, departmentOptions] = await Promise.all([
          garageAPI.getConfigOptions('user_profile_role'),
          garageAPI.getConfigOptions('team'),
          garageAPI.getConfigOptions('department'),
        ]);

        // Group roles by category_type (leadership/operations)
        const leadership = roleOptions
          .filter(r => r.categoryType === 'leadership')
          .map(r => ({ id: r.value, label: r.label }));

        const operations = roleOptions
          .filter(r => r.categoryType === 'operations')
          .map(r => ({ id: r.value, label: r.label }));

        setDynamicRoles({ leadership, operations });
        setDynamicTeams(teamOptions.map(t => ({ id: t.value, label: t.label })));
        setDynamicDepartments(
          (departmentOptions || []).map((d) => ({ id: d.value, label: d.label || d.value }))
        );
      } catch (error) {
        console.error('Failed to load dynamic options:', error);
        // Fallback to hardcoded options
        setDynamicRoles(ROLE_OPTIONS);
        setDynamicTeams(TEAM_OPTIONS);
        setDynamicDepartments([]);
      } finally {
        setRolesLoading(false);
      }
    };

    loadDynamicOptions();
  }, [configVersion]);

  // Load user profile
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await garageAPI.getUserProfile(userEmail);
        setUserProfile(profile);
        const { firstName: fn, lastName: ln } = resolveNameFields(profile, userName);
        setFirstName(fn);
        setLastName(ln);
        setSelectedTeam(profile?.persona || null);
        setSelectedDepartment(profile?.department || null);
        setWorkdayBusinessUnit(profile?.workdayBusinessUnit || null);
        setSelectedRole(profile?.userRole || null);
        setSelectedOrganization(profile?.preferences?.hub_organization || null);
      } catch (error) {
        console.error('Failed to load user profile:', error);
        // Still show sidebar name if profile fetch fails
        const { firstName: fn, lastName: ln } = resolveNameFields(null, userName);
        setFirstName(fn);
        setLastName(ln);
      } finally {
        setLoading(false);
      }
    };

    if (userEmail) {
      loadProfile();
    }
  }, [userEmail, userName]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const profileData = {
        firstName: firstName || null,
        lastName: lastName || null,
        displayName:
          [firstName, lastName].filter(Boolean).join(' ') ||
          userProfile?.displayName ||
          userName ||
          null,
        persona: selectedTeam,
        userRole: selectedRole,
        preferences: {
          ...userProfile?.preferences,
          hub_organization: selectedOrganization,
        }
      };

      await garageAPI.updateUserProfile(userEmail, profileData);

      // Update local state
      setUserProfile({ ...userProfile, ...profileData });

      // Notify parent component to update profile across all views
      if (onProfileUpdate) {
        await onProfileUpdate(profileData);
      }

      setAlertModal({
        isOpen: true,
        title: 'Success',
        message: 'Settings saved successfully!',
        variant: 'success'
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to save settings. Please try again.',
        variant: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRerunOnboarding = async () => {
    setConfirmModal({
      isOpen: true,
      title: 'Reset Personalization',
      message: 'This will reset your personalization and show the onboarding wizard again. Continue?',
      variant: 'warning',
      onConfirm: async () => {
        try {
          // Reset onboarding status in database
          await garageAPI.updateUserProfile(email, {
            onboardingStatus: 'pending',
            preferences: {
              ...userProfile?.preferences,
              onboarding_reset: true,
              reset_at: new Date().toISOString()
            }
          });
          setConfirmModal({ ...confirmModal, isOpen: false });
          // Reload page to trigger onboarding check
          window.location.reload();
        } catch (error) {
          console.error('Failed to reset onboarding:', error);
          setConfirmModal({ ...confirmModal, isOpen: false });
          setAlertModal({
            isOpen: true,
            title: 'Error',
            message: 'Failed to reset onboarding. Please try again.',
            variant: 'error'
          });
        }
      }
    });
  };

  const handleResetEverything = async () => {
    setConfirmModal({
      isOpen: true,
      title: 'Reset Everything',
      message: 'This will clear all your personalization settings. Are you sure?',
      variant: 'danger',
      onConfirm: async () => {
        setFirstName('');
        setLastName('');
        setSelectedTeam(null);
        setSelectedRole(null);
        setSelectedOrganization(null);
        setConfirmModal({ ...confirmModal, isOpen: false });
      }
    });
  };

  if (loading) {
    return (
      <div className="garage-content">
        <GarageLoadingScreen message="Loading settings..." />
      </div>
    );
  }

  return (
    <div className="garage-content">
      <div className="garage-page-hero">
        <div>
          <h1 className="garage-page-title">Settings</h1>
          <p className="garage-page-subtitle">Tell Ringo who you are and how you want to use it. We'll personalize accordingly.</p>
        </div>
      </div>

      <div className="settings-container">
        {/* Identity Section */}
        <div className="settings-section">
          <div className="settings-section-header">
            <UserIcon size={20} />
            <h2>Your Identity</h2>
          </div>

          <div className="settings-field-group">
            <div className="settings-field">
              <label htmlFor="firstName">First Name</label>
              <input
                id="firstName"
                type="text"
                placeholder="Your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="settings-input"
              />
            </div>
            <div className="settings-field">
              <label htmlFor="lastName">Last Name</label>
              <input
                id="lastName"
                type="text"
                placeholder="Your last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="settings-input"
              />
            </div>
          </div>

          <div className="settings-field">
            <label htmlFor="team">Which team are you on?</label>
            <select
              id="team"
              value={selectedTeam || ''}
              onChange={(e) => setSelectedTeam(e.target.value || null)}
              className="settings-select"
              disabled={rolesLoading}
            >
              <option value="">Select your team...</option>
              {dynamicTeams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.label}
                </option>
              ))}
            </select>
          </div>

          <div className="settings-field">
            <label htmlFor="department">Department</label>
            <input
              id="department"
              type="text"
              className="settings-input"
              value={departmentLabel}
              readOnly
              disabled
            />
            <p className="settings-field-hint">
              {workdayBusinessUnit
                ? `Set from Workday (${workdayBusinessUnit}) and managed by an admin. `
                : 'Set from Workday and managed by an admin. '}
              Controls which tools you see when onboarding filters are cleared.
            </p>
          </div>

          <div className="settings-field">
            <label htmlFor="role">What's your role?</label>
            <select
              id="role"
              value={selectedRole || ''}
              onChange={(e) => setSelectedRole(e.target.value || null)}
              className="settings-select"
              disabled={rolesLoading}
            >
              <option value="">Select your role...</option>
              {dynamicRoles.leadership.length > 0 && (
                <optgroup label="Leadership">
                  {dynamicRoles.leadership.map((role) => (
                    <option key={role.id} value={role.id}>{role.label}</option>
                  ))}
                </optgroup>
              )}
              {dynamicRoles.operations.length > 0 && (
                <optgroup label="Operations">
                  {dynamicRoles.operations.map((role) => (
                    <option key={role.id} value={role.id}>{role.label}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
        </div>

        {/* Start Page Section */}
        <div className="settings-section">
          <div className="settings-section-header">
            <h2>Hub Organization</h2>
          </div>

          <div className="settings-field">
            <label htmlFor="organization">How should we organize your Hub?</label>
            <select
              id="organization"
              value={selectedOrganization || ''}
              onChange={(e) => setSelectedOrganization(e.target.value || null)}
              className="settings-select"
            >
              <option value="">Select organization...</option>
              {HUB_ORGANIZATION_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label} — {option.description}
                </option>
              ))}
            </select>
            <p className="settings-field-hint">You can change this anytime with the Group dropdown in the Hub.</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="settings-actions">
          <button
            className="settings-btn settings-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>

          <button
            className="settings-btn settings-btn-secondary"
            onClick={handleRerunOnboarding}
          >
            <RefreshCw size={16} />
            Rerun Onboarding
          </button>

          <button
            className="settings-btn settings-btn-danger"
            onClick={handleResetEverything}
          >
            <RotateCcw size={16} />
            Reset Everything
          </button>

          <button
            type="button"
            className="settings-btn settings-btn-secondary settings-btn-signout"
            onClick={handleSignOut}
            disabled={signingOut}
            title="Sign out of AI Garage on this browser. You can sign in again with SSO."
          >
            <LogOut size={16} />
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
    </div>
  );
};

export default GarageSettingsView;
